import * as React from "react";
import { lazy, Suspense } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dot, type DotTone } from "@/components/ui/dot";
import { OeeBreakdown } from "@/features/oee/OeeBreakdown";
import { useOee } from "@/features/oee/hooks";
import { useLineAlarms } from "@/features/alarms/hooks";
import { formatRelativeTime } from "@/lib/display-labels";
import { LineMachinesSummaryStats } from "./LineMachinesSummaryStats";
import type { LineSummary, MachineDetail } from "@/types/lines.types";

const ProductionLineCanvas = lazy(() =>
  import("@/features/lines/canvas/ProductionLineCanvas").then((m) => ({
    default: m.ProductionLineCanvas,
  }))
);

const SEV_TONE: Record<string, DotTone> = { HIGH: "danger", MEDIUM: "warning", LOW: "neutral" };

function AlarmsTab({ lineId }: { lineId: string }): React.ReactElement {
  const { data } = useLineAlarms(lineId);
  const active = (data ?? []).filter(
    (a) => a.state === "UNACKNOWLEDGED" || a.state === "ACKNOWLEDGED"
  );
  if (active.length === 0) {
    return (
      <p className="px-1 py-4 text-[13px] text-text-tertiary">Nenhum alarme ativo nesta linha.</p>
    );
  }
  return (
    <ul className="divide-y divide-border-subtle">
      {active.map((a) => (
        <li key={a.id} className="flex items-center gap-2 py-2 text-[12px]">
          <Dot tone={SEV_TONE[a.severity] ?? "neutral"} />
          <span className="flex-1 text-text-primary">{a.title}</span>
          <span className="text-text-tertiary">{a.machine?.name}</span>
          <span className="font-mono text-text-tertiary">{formatRelativeTime(a.startAt)}</span>
        </li>
      ))}
    </ul>
  );
}

export function LineCardTabs({
  line,
  onMachineClick,
}: {
  line: LineSummary;
  onMachineClick: (m: MachineDetail) => void;
}): React.ReactElement {
  const { data: oee } = useOee(line.id, "24h", { breakdown: true });

  return (
    <Tabs defaultValue="maquinas">
      <TabsList>
        <TabsTrigger value="maquinas">Máquinas</TabsTrigger>
        <TabsTrigger value="oee">OEE detalhado</TabsTrigger>
        <TabsTrigger value="alarmes">Alarmes</TabsTrigger>
      </TabsList>

      <TabsContent value="maquinas">
        <Suspense
          fallback={
            <div className="bg-bg-muted-rd/40 flex h-[200px] items-center justify-center rounded-md border text-sm text-text-tertiary">
              Carregando fluxo…
            </div>
          }
        >
          <ProductionLineCanvas
            lineId={line.id}
            mode="display"
            onMachineClick={onMachineClick}
            height={200}
            compact
          />
        </Suspense>
        {}
        <LineMachinesSummaryStats line={line} />
      </TabsContent>

      <TabsContent value="oee">
        {oee ? (
          <OeeBreakdown oee={oee} lineId={line.id} />
        ) : (
          <p className="px-1 py-4 text-[13px] text-text-tertiary">
            OEE indisponível para esta linha.
          </p>
        )}
      </TabsContent>

      <TabsContent value="alarmes">
        <AlarmsTab lineId={line.id} />
      </TabsContent>
    </Tabs>
  );
}
